import { Icon, useDisclosure } from '@chakra-ui/react';
import type { DiscountApplicationMethodEnum } from '@monetize/types/app';
import {
  AmountUnitTypeEnum,
  CustomSelectItem,
  IDiscount,
  IQuoteDiscountRespSchema,
  IQuoteOfferingReqSchema,
  IQuoteOfferingRespSchema,
  ProductTypeEnum,
} from '@monetize/types/app';
import find from 'lodash/find';
import { useEffect, useRef, useState } from 'react';
import { Control, Controller, UseFormSetValue } from 'react-hook-form';
import { MdSettings } from 'react-icons/md';
import {
  MBox,
  MButton,
  MFlex,
  MFormField,
  MLockedTextOrContent,
  MTooltip,
} from '../../../../../components/Monetize';
import {
  MCustomMultiSelect,
  MCustomMultiSelectRef,
} from '../../../../../components/Monetize/MCustomSelect/MCustomMultiSelect';
import { MSearchInputWithClear } from '../../../../../components/Monetize/MSearchInputWithClear';
import {
  CUSTOM_SELECT_SUBTITLE_TYPE,
  OPTION_TYPE_FIELD,
} from '../../../../../constants/customSelect';
import QuoteOfferingCustomDiscountModal from './QuoteOfferingCustomDiscountModal';

export interface QuoteOfferingDiscountProps {
  isOpen: boolean;
  currency: string;
  quoteOffering?: IQuoteOfferingRespSchema | null;
  errors: any;
  control: Control<IQuoteOfferingReqSchema>;
  offeringId: string;
  rateId?: string | null;
  discounts: (IDiscount | IQuoteDiscountRespSchema)[] | null;
  discountIds?: string[] | null;
  isRemoved?: boolean;
  disabled?: boolean;
  isReadOnly?: boolean;
  handleQuoteOfferingWithoutDirtyCheck: () => void;
  setValue: UseFormSetValue<IQuoteOfferingReqSchema>;
  discountDisabledProductIds: string[];
  discountApplicationMethod?: DiscountApplicationMethodEnum | null;
}

export const QuoteOfferingDiscount = ({
  isOpen,
  currency,
  quoteOffering,
  errors,
  control,
  offeringId,
  rateId,
  discounts,
  discountIds,
  isRemoved,
  disabled,
  isReadOnly,
  handleQuoteOfferingWithoutDirtyCheck,
  setValue,
  discountDisabledProductIds,
  discountApplicationMethod,
}: QuoteOfferingDiscountProps) => {
  const customSelectRef = useRef<MCustomMultiSelectRef>(undefined);
  const [searchTerm, setSearchTerm] = useState('');
  const [visibleItems, setVisibleItems] = useState(discounts || []);
  const [selectedItemCount, setSelectedItemCount] = useState(0);
  // Drives the merged group's focus halo. `_focusWithin` would also fire
  // when the settings button is focused, but that button has its own halo
  // and shouldn't trigger the whole-group ring — so we track the input's
  // own focus explicitly instead.
  const [isDiscountInputFocused, setIsDiscountInputFocused] = useState(false);
  const {
    open: isCustomDiscountModalOpen,
    onOpen: openCustomDiscountModal,
    onClose,
  } = useDisclosure();

  useEffect(() => {
    if (!discounts || !searchTerm) {
      setVisibleItems(discounts || []);
    }
    const searchTermLowercase = (searchTerm || '').toLowerCase();
    setVisibleItems(() =>
      (discounts || []).filter(({ name }) =>
        (name || '').toLowerCase().includes(searchTermLowercase),
      ),
    );
  }, [discounts, searchTerm]);

  useEffect(() => {
    // if discountIds changes, use that value
    // otherwise update based on user selection in real time
    setSelectedItemCount(discountIds?.length || 0);
  }, [discountIds]);

  if (!quoteOffering?.items?.length || !offeringId || !rateId || !discounts) {
    return <MBox />;
  }

  const discountableItems = quoteOffering.items.filter(
    ({ productType, productId }) =>
      productType !== ProductTypeEnum.ONETIME_PREPAID_CREDIT &&
      !discountDisabledProductIds.includes(productId),
  );

  if (discountableItems.length === 0) {
    return <MBox />;
  }

  const eligibleItemsForDiscount = discountableItems.filter(
    ({ productType }) => productType !== ProductTypeEnum.USAGE,
  );

  const getDiscountPlaceholder = () => {
    if (discountIds && discountIds?.length > 0) {
      if (discountIds?.length === 1) {
        // find is returning types other than IDiscount. Use any to stop it complaining about accessing name in return.
        const selected: any = find(discounts, {
          id: discountIds[0],
        });
        return selected
          ? selected?.name
          : `${discountIds.length} Discounts Selected`;
      }
      return `${discountIds.length} Discounts Selected`;
    }
    return 'Select';
  };

  const hasCustomDiscount = discountableItems.some(
    ({ customDiscountAmountOrPercent }) => !!customDiscountAmountOrPercent,
  );
  const hasCatalogDiscount = !!(discountIds && discountIds.length > 0);

  const getCollapsedDiscount = () => {
    if (hasCustomDiscount && hasCatalogDiscount) {
      return 'Custom & Standard';
    } else if (hasCustomDiscount) {
      return 'Custom Discount';
    } else if (discountIds && discountIds?.length === 1) {
      return discounts.find(({ id }) => id === discountIds[0])?.name;
    } else if (discountIds && discountIds?.length > 1) {
      return `${discountIds.length} Discounts`;
    } else {
      return '';
    }
  };

  function handleDiscountFilterChange(value: string) {
    setSearchTerm(value);
  }

  function handleClear() {
    customSelectRef.current?.clearAll();
    setSelectedItemCount(0);
  }

  function handleSelectAll() {
    customSelectRef.current?.updateItems(visibleItems.map(({ id }) => id));
  }

  function handleAddOrRemoveItem(value: string, allValues?: string[]) {
    setSelectedItemCount(allValues?.length || 0);
  }

  function handleOpenStateChange(_isOpen: boolean) {
    if (!_isOpen) {
      setSearchTerm('');
      setSelectedItemCount(discountIds?.length || 0);
    }
  }

  function handleCustomDiscountSaveOnOffering(discountPercentage: number) {
    eligibleItemsForDiscount?.forEach((item) => {
      if (item.id) {
        // Find the actual index in the original items array
        const actualIndex = quoteOffering?.items?.findIndex(
          (originalItem) => originalItem.id === item.id,
        );
        if (actualIndex !== undefined && actualIndex !== -1) {
          setValue(
            `items.${actualIndex}.customDiscountAmountOrPercent`,
            discountPercentage,
            { shouldDirty: true },
          );
          setValue(
            `items.${actualIndex}.customDiscountType`,
            AmountUnitTypeEnum.PERCENTAGE,
            { shouldDirty: true },
          );
        }
      }
    });

    handleQuoteOfferingWithoutDirtyCheck();
  }

  const discountPrependItems: CustomSelectItem[] = [];

  if (discounts?.length > 0) {
    discountPrependItems.push({
      item: {
        id: 'EXISTING_DISCOUNTS',
        name: 'EXISTING DISCOUNTS',
        [OPTION_TYPE_FIELD]: CUSTOM_SELECT_SUBTITLE_TYPE,
      },
      isHeading: true,
      render: () => (
        <MSearchInputWithClear
          heading="EXISTING DISCOUNTS"
          placeholder="Search Discounts"
          value={searchTerm}
          selectedItemCount={selectedItemCount}
          visibleItemCount={visibleItems.length || 0}
          onChange={handleDiscountFilterChange}
          onClearAll={handleClear}
          onSelectAll={handleSelectAll}
        />
      ),
    });
  }

  return (
    <>
      <MFormField error={errors.discountIds} alignSelf="end">
        <MLockedTextOrContent
          text={getCollapsedDiscount()}
          h={8}
          textProps={{ lineClamp: 1 }}
          isLocked={isRemoved || isReadOnly || !isOpen}
          blankText=""
        >
          <Controller
            name="discountIds"
            control={control}
            defaultValue={discountIds}
            render={({ field: { onChange, ref, ...rest } }) => (
              // v3's `InputGroup` accepts a single input child only; this
              // stands in for v2's, which was a relative, isolated
              // full-width flex row around the select and settings button.
              <MFlex
                w="100%"
                position="relative"
                isolation="isolate"
                alignItems="center"
                borderRadius="sm"
                // Focus halo lives on the whole group, not just the input,
                // so it wraps the merged input + settings-button control as
                // one unit instead of stopping at the input's own edge. Tied
                // to the input's own focus state (not `_focusWithin`) so
                // tabbing to the settings button shows only the button's own
                // halo instead of the whole group lighting up too.
                boxShadow={isDiscountInputFocused ? 'focus' : undefined}
              >
                <MCustomMultiSelect
                  ref={(refEvent: any) => {
                    ref(refEvent);
                    customSelectRef.current = refEvent;
                  }}
                  isLazy
                  usePortal
                  isDisabled={isRemoved || disabled}
                  items={visibleItems}
                  itemValue="id"
                  itemTitle="name"
                  onChange={(e) => {
                    onChange(e);
                    handleQuoteOfferingWithoutDirtyCheck();
                    setSearchTerm('');
                  }}
                  onRemove={handleAddOrRemoveItem}
                  onAdd={handleAddOrRemoveItem}
                  onOpenStateChange={handleOpenStateChange}
                  closeButtonText="Apply"
                  placeholder={getDiscountPlaceholder()}
                  popOverContentProps={{ minWidth: '300px' }}
                  inputProps={{
                    _placeholder: { color: 'tPurple.dark' },
                    _disabled: {
                      // By default we hide placeholder when disabled, but here we want it visible
                      _placeholder: {
                        color: 'tGray.darkPurple',
                      },
                    },
                    // The group above owns the focus halo so it can wrap the
                    // whole merged control; suppress the input's own halo so
                    // there isn't a second, smaller ring drawn inside it.

                    _focus: { boxShadow: 'none' },
                    _focusVisible: { boxShadow: 'none' },
                    onFocus: () => setIsDiscountInputFocused(true),
                    onBlur: () => setIsDiscountInputFocused(false),

                    paddingRight: 10,
                    borderRightWidth: 0,
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                  }}
                  prependItemsOutsideScrollArea
                  prependItems={discountPrependItems}
                  isReadOnly={isReadOnly}
                  flex={1}
                  {...rest}
                />
                <MTooltip
                  label="Usage products are not eligible for offering level custom discounts"
                  isDisabled={eligibleItemsForDiscount.length > 0}
                >
                  <MButton
                    variant="neutral"
                    h="30px"
                    w="30px"
                    minWidth="auto"
                    px={2}
                    borderColor="border.default"
                    borderTopLeftRadius={0}
                    borderBottomLeftRadius={0}
                    onClick={openCustomDiscountModal}
                    isDisabled={
                      isRemoved ||
                      isReadOnly ||
                      disabled ||
                      !eligibleItemsForDiscount.length
                    }
                  >
                    <Icon boxSize={3.5} asChild>
                      <MdSettings />
                    </Icon>
                  </MButton>
                </MTooltip>
              </MFlex>
            )}
          />
        </MLockedTextOrContent>
      </MFormField>
      {isCustomDiscountModalOpen && (
        <QuoteOfferingCustomDiscountModal
          isOpen={isCustomDiscountModalOpen}
          quoteOffering={quoteOffering}
          eligibleItemsForDiscount={eligibleItemsForDiscount}
          onClose={onClose}
          onChangeValue={handleCustomDiscountSaveOnOffering}
          targetAmountOptions={{
            currency,
            discountApplicationMethod,
          }}
        />
      )}
    </>
  );
};

export default QuoteOfferingDiscount;
